import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getGraphicQueryOptions } from '@/features/graphics/api/get-graphic/get-graphic.hook';
import { getVideoQueryOptions } from '@/features/videos/api/get-video/get-video.hook';
import {
  type MetaAdStatus,
  metaAdStatusLabels,
} from '@borradh-workspace/labels';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  MapPin,
  Pencil,
  Play,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { AdCardEditButton } from './ad-card-edit-button';
import { AdCardEditTargetContext } from './ad-card-edit-context';
import { useSavedAdPreview } from './use-saved-ad-preview';

import { VideoPreviewDialog } from './video-preview-dialog';

/**
 * Combined ad-creative card rendered when `createDraftAd` or `executeLaunchAd`
 * returns. The creative (a video or an offer graphic) sits on top as a clean
 * media box; the ad copy — headline, caption, CTA — sits beneath it.
 *
 * The creative may still be rendering when the card mounts (the create-campaign
 * flow kicks off the render and the draft ad in the same turn). The card polls
 * the video / graphic and shows a pulsing skeleton until it's ready, then swaps
 * the whole card in at once — copy is already present, so the only thing it
 * waits on is the media (the "wait for everything" behaviour).
 *
 * Campaign / budget / targeting metadata render when display fields are passed.
 * Combined campaign proposals also include their saved offer and form settings.
 *
 * Variants:
 *   - 'draft':    the operator can still rewrite / cancel via chat.
 *   - 'launched': the launched confirmation — shows a "Live" pill.
 */
/**
 * Honest launch state read back from Meta after the launch (ADR-005).
 * Mirrors `adLaunchStateValues` in
 * `packages/features/src/meta-ads/services/verify-ad-launch-state/`.
 */
export type AdLaunchCardState =
  | 'live'
  | 'live_but_campaign_paused'
  | 'pending_review'
  | 'paused_at_meta'
  | 'rejected'
  | 'failed'
  | 'unverified'
  // Idempotency no-op (Phase 4 #95): the ad was already launched, so this call
  // created nothing. The pill says "Already live" and the state is a re-read.
  | 'already_live';

export interface AdPreviewProps {
  variant: 'draft' | 'launched';
  /** Current owned saved-ad state; a proposal or accepted approval is not proof of launch. */
  savedAdStatus?: MetaAdStatus | 'loading' | 'unavailable';
  /**
   * What produced this card. A LAUNCH puts an ad on Meta for the first time;
   * an EDIT changes copy on an ad that is already there. Both render the
   * 'launched' variant, but the no-read-back fallback has to say different
   * things: "Submitted to Meta" is right after a launch and simply wrong after
   * editing an ad that has been delivering for a week. Defaults to 'launch',
   * so every existing caller keeps its current copy. (ENG-631)
   */
  context?: 'launch' | 'edit';
  /**
   * Verified state for the 'launched' variant. When absent on a launched
   * card, the card renders the conservative "Submitted" pill — it never
   * claims "Live" without a read-back.
   */
  launchState?: AdLaunchCardState;
  adName: string;
  adId?: string;
  onSendMessage?: (text: string) => void;
  headline?: string;
  primaryText?: string;
  description?: string;
  callToAction?: string;
  campaignName?: string;
  videoTitle?: string;
  videoId?: string;
  graphicId?: string;
  /**
   * A library / uploaded IMAGE asset used as ad creative — rendered directly
   * from this URL (no polling; unlike videoId/graphicId there's no async
   * render to wait on).
   */
  assetImageUrl?: string;
  /** A library / uploaded VIDEO asset used as ad creative — rendered directly. */
  assetVideoUrl?: string;
  /** Thumbnail for a library / uploaded VIDEO asset. */
  assetThumbnailUrl?: string;
  budgetDisplay?: string;
  targetingDisplay?: string;
  destinationUrl?: string;
  promotion?: { id?: string; name: string };
  leadForm?: { id?: string; name: string };
  /** Saved offer and campaign settings accompanying a combined proposal. */
  details?: {
    title: string;
    fields: { label: string; value: string }[];
  }[];
}

export function formatCta(cta: string): string {
  return cta
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}

const POLL_INTERVAL = 4_000; // 4s — match the graphic/video status cards.

/**
 * Pill + optional notice per verified launch state. `undefined` state (old
 * payloads / unverified deploys) gets the conservative "Submitted" pill —
 * legacy ACTIVE state names do not establish delivery or spend.
 */
export function launchStateDisplay(
  state: AdLaunchCardState | undefined,
  context: 'launch' | 'edit' = 'launch'
): {
  pill: string;
  pillVariant: 'default' | 'secondary' | 'outline' | 'destructive';
  notice?: string;
} {
  switch (state) {
    case 'live':
      return {
        pill: 'Ad active',
        pillVariant: 'default',
        notice:
          'Meta reports this ad as ACTIVE. Delivery and spend have not been verified.',
      };
    case 'already_live':
      return {
        pill: 'Existing ad active',
        pillVariant: 'default',
        notice:
          'No new ad was created. Delivery and spend have not been verified.',
      };
    case 'live_but_campaign_paused':
      return {
        pill: 'Campaign blocked',
        pillVariant: 'secondary',
        notice:
          'Meta reports a campaign status that blocks delivery. Check its status before resuming.',
      };
    case 'pending_review':
      return {
        pill: 'In Meta review',
        pillVariant: 'secondary',
        notice:
          'Meta is reviewing this ad. It will not deliver until approved.',
      };
    case 'paused_at_meta':
      return {
        pill: 'Paused at Meta',
        pillVariant: 'secondary',
        notice: 'This ad is paused on Meta and is not delivering.',
      };
    case 'rejected':
      return {
        pill: 'Rejected',
        pillVariant: 'destructive',
        notice: 'Meta rejected this ad. It is not running.',
      };
    case 'failed':
      return {
        pill: 'Not running',
        pillVariant: 'destructive',
        notice: 'Meta reports an issue with this ad. It is not running.',
      };
    default:
      // No read-back. Stay conservative — never claim "Active" here — but say
      // which thing was not verified. After an edit the ad was already on
      // Meta, so "Submitted to Meta" would describe a launch that never
      // happened.
      return context === 'edit'
        ? {
            pill: 'Edit submitted',
            pillVariant: 'outline',
            notice:
              'The change was submitted to Meta. The ad’s current status, delivery and spend have not been verified.',
          }
        : {
            pill: 'Submitted',
            pillVariant: 'outline',
            notice:
              'Submitted to Meta — the ad’s status has not been verified.',
          };
  }
}

/**
 * The status pill for an ad, from whatever is known about it.
 *
 * ONE definition for the chat row, the chat card and the side panel: the
 * saved record wins when it has loaded, then a launched card's read-back
 * state, and a draft is simply "Draft". Three copies of this ladder would
 * drift the way the two chat surfaces' trace copy once did.
 */
export function describeAdStatus({
  variant,
  savedAdStatus,
  launchState,
  context,
}: Pick<
  AdPreviewProps,
  'variant' | 'savedAdStatus' | 'launchState' | 'context'
>): {
  label: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  notice?: string;
} {
  if (savedAdStatus === 'loading')
    return { label: 'Checking status', variant: 'secondary' };
  if (savedAdStatus === 'unavailable')
    return { label: 'Status unavailable', variant: 'secondary' };
  if (savedAdStatus) {
    return {
      label: metaAdStatusLabels[savedAdStatus],
      variant:
        savedAdStatus === 'error' || savedAdStatus === 'rejected'
          ? 'destructive'
          : 'secondary',
    };
  }
  if (variant === 'launched') {
    const stateDisplay = launchStateDisplay(launchState, context);
    return {
      label: stateDisplay.pill,
      variant: stateDisplay.pillVariant,
      notice: stateDisplay.notice,
    };
  }
  return { label: 'Draft', variant: 'secondary' };
}

function AdPreviewCardContent({
  variant,
  savedAdStatus,
  context,
  launchState,
  adName,
  onSendMessage,
  headline,
  primaryText,
  description,
  callToAction,
  campaignName,
  videoTitle,
  videoId,
  graphicId,
  assetImageUrl,
  assetVideoUrl,
  assetThumbnailUrl,
  budgetDisplay,
  targetingDisplay,
  destinationUrl,
  promotion,
  leadForm,
  details,
}: AdPreviewProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [failedThumbnailUrl, setFailedThumbnailUrl] = useState<string>();

  const hasDirectAsset = !!(assetImageUrl || assetVideoUrl);

  // Poll the creative until it's rendered. Only one of videoId / graphicId is
  // set per ad. `refetchInterval` stops once the row reaches a terminal state.
  const videoQuery = useQuery({
    ...getVideoQueryOptions(videoId ?? ''),
    enabled: !!videoId && !hasDirectAsset,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'ready' || status === 'failed' ? false : POLL_INTERVAL;
    },
  });
  const graphicQuery = useQuery({
    ...getGraphicQueryOptions(graphicId ?? ''),
    enabled: !!graphicId && !hasDirectAsset,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'ready' || status === 'failed' ? false : POLL_INTERVAL;
    },
  });

  const video = videoQuery.data;
  const graphic = graphicQuery.data;

  // A library / uploaded asset renders directly from its resolved URL — no
  // polling and no skeleton (there's no async render to wait on). It flows
  // through the same computed vars below so the markup stays single-path.

  // Legacy videoId can hold an image asset ID; resolved media wins.
  const isVideo = !!assetVideoUrl || (!assetImageUrl && !!videoId);
  const thumbnailUrl = video?.thumbnailUrl ?? assetThumbnailUrl ?? undefined;
  const blobUrl = video?.blobUrl ?? assetVideoUrl ?? undefined;
  const imageUrl =
    assetImageUrl ?? graphic?.outputs?.find((o) => o.status !== 'failed')?.url;

  const isLaunched = variant === 'launched';
  const stateDisplay = launchStateDisplay(launchState, context);
  const { label: statusLabel, variant: statusVariant } = describeAdStatus({
    variant,
    savedAdStatus,
    launchState,
    context,
  });
  const hasMeta = !!(
    campaignName ||
    budgetDisplay ||
    targetingDisplay ||
    destinationUrl ||
    leadForm
  );

  const mediaReady = hasDirectAsset
    ? true
    : isVideo
      ? video?.status === 'ready' && !!blobUrl
      : !!imageUrl && graphic?.status === 'ready';
  const mediaFailed = hasDirectAsset
    ? false
    : isVideo
      ? video?.status === 'failed'
      : graphic?.status === 'failed';

  // The creative render failed — surface it instead of an endless skeleton.
  if (mediaFailed) {
    return (
      <div
        data-testid="claire-card-ad-preview"
        className="w-full rounded-lg border border-destructive/30 bg-card p-4 text-xs text-muted-foreground sm:max-w-md"
      >
        The {isVideo ? 'video' : 'graphic'} for this ad couldn&apos;t be
        rendered. Try again, or tweak the request.
      </div>
    );
  }

  // Wait for the media — show a card-shaped skeleton (a square for the
  // creative, lines for the caption) until the creative is rendered, then drop
  // the finished card in all at once.
  if ((videoId || graphicId) && !mediaReady) {
    return (
      <article
        aria-label={adName}
        data-testid="claire-card-ad-preview"
        className="w-full overflow-hidden rounded-lg border bg-card sm:max-w-md"
      >
        <div className="flex items-start justify-between gap-3 border-b px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-1">
            <h3 className="min-w-0 text-xs font-medium">{adName}</h3>
            <AdCardEditButton
              adName={adName}
              field="ad name"
              onSendMessage={onSendMessage}
            />
          </div>
          <Badge variant={statusVariant} className="shrink-0 text-[10px]">
            {!savedAdStatus && launchState === 'live' && (
              <CheckCircle2 className="mr-1 size-3" />
            )}
            {statusLabel}
          </Badge>
        </div>
        <Skeleton className="aspect-square w-full rounded-none" />
        <div className="space-y-2 p-3">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="mt-1 h-5 w-20 rounded-full" />
        </div>
      </article>
    );
  }

  return (
    <>
      <article
        aria-label={adName}
        data-testid="claire-card-ad-preview"
        className="w-full overflow-hidden rounded-lg border bg-card sm:max-w-md"
      >
        <div className="flex items-start justify-between gap-3 border-b px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-1">
            <h3 className="min-w-0 text-xs font-medium">{adName}</h3>
            <AdCardEditButton
              adName={adName}
              field="ad name"
              onSendMessage={onSendMessage}
            />
          </div>
          <Badge variant={statusVariant} className="shrink-0 text-[10px]">
            {!savedAdStatus && launchState === 'live' && (
              <CheckCircle2 className="mr-1 size-3" />
            )}
            {statusLabel}
          </Badge>
        </div>
        <div className="relative">
          {/* Creative */}
          {isVideo ? (
            <button
              type="button"
              onClick={() => blobUrl && setPreviewOpen(true)}
              disabled={!blobUrl}
              aria-label={videoTitle ?? adName}
              className="relative block aspect-square w-full overflow-hidden bg-muted disabled:cursor-default"
            >
              {thumbnailUrl && failedThumbnailUrl !== thumbnailUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={thumbnailUrl}
                  alt={videoTitle ?? adName}
                  onError={() => setFailedThumbnailUrl(thumbnailUrl)}
                  className="size-full object-cover"
                />
              ) : blobUrl ? (
                <video
                  src={blobUrl}
                  aria-hidden="true"
                  tabIndex={-1}
                  muted
                  playsInline
                  preload="metadata"
                  className="pointer-events-none size-full object-cover"
                  onLoadedMetadata={(event) => {
                    const video = event.currentTarget;
                    // Decode a visible frame without autoplaying the preview tile.
                    video.currentTime =
                      Number.isFinite(video.duration) && video.duration > 0
                        ? Math.min(0.1, video.duration / 2)
                        : 0.1;
                  }}
                />
              ) : (
                <Play className="absolute inset-0 m-auto size-8 text-muted-foreground/50" />
              )}
              {blobUrl && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30">
                  <Play className="size-10 fill-white text-white drop-shadow" />
                </div>
              )}
            </button>
          ) : (
            imageUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={imageUrl}
                alt={adName}
                className="w-full"
                loading="lazy"
              />
            )
          )}

          <div className="absolute right-2 top-2 rounded-sm bg-background/90">
            <AdCardEditButton
              adName={adName}
              field="creative"
              onSendMessage={onSendMessage}
            />
          </div>
        </div>
        {/* Copy */}
        <div className="space-y-1.5 p-3">
          {!savedAdStatus && isLaunched && stateDisplay.notice ? (
            <p className="text-[11px] leading-snug text-muted-foreground">
              {stateDisplay.notice}
            </p>
          ) : null}
          {[
            {
              field: 'headline',
              value: headline,
              className: 'text-sm font-semibold leading-snug',
            },
            {
              field: 'caption / ad copy',
              value: primaryText,
              className:
                'whitespace-pre-line text-xs leading-relaxed text-muted-foreground',
            },
            {
              field: 'description',
              value: description,
              className: 'whitespace-pre-line text-xs text-muted-foreground',
            },
            {
              field: 'call to action',
              value: callToAction ? formatCta(callToAction) : undefined,
              className: 'text-xs',
            },
          ].map(({ field, value, className }) =>
            value || onSendMessage ? (
              <div
                key={field}
                className="flex items-start justify-between gap-2"
              >
                <p className={`min-w-0 break-words pt-1 ${className}`}>
                  {value || `Add ${field}`}
                </p>
                <AdCardEditButton
                  adName={adName}
                  field={field}
                  onSendMessage={onSendMessage}
                />
              </div>
            ) : null
          )}

          {/* Campaign / budget / targeting — only when provided. */}
          {hasMeta && (
            <div className="mt-2 space-y-1 border-t pt-2">
              {campaignName && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-[10px]">
                    {campaignName}
                  </Badge>
                  <AdCardEditButton
                    adName={adName}
                    field="campaign"
                    onSendMessage={onSendMessage}
                  />
                </div>
              )}
              {budgetDisplay && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Wallet className="size-3.5 shrink-0" />
                  <span>{budgetDisplay}</span>
                  <AdCardEditButton
                    adName={adName}
                    field="budget"
                    onSendMessage={onSendMessage}
                  />
                </div>
              )}
              {targetingDisplay && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="size-3.5 shrink-0" />
                  <span>{targetingDisplay}</span>
                  <AdCardEditButton
                    adName={adName}
                    field="targeting"
                    onSendMessage={onSendMessage}
                  />
                </div>
              )}
              {/* A lead-form ad opens its instant form; the website Meta
                  requires on the creative is never where a tap goes, so
                  showing it here reads as "this ad sends people to my site". The
                  form's edit control lives under View details. */}
              {leadForm ? (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ClipboardList className="size-3.5 shrink-0" />
                  <span className="truncate">Lead form: {leadForm.name}</span>
                </div>
              ) : destinationUrl ? (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ExternalLink className="size-3.5 shrink-0" />
                  <span className="truncate">{destinationUrl}</span>
                  <AdCardEditButton
                    adName={adName}
                    field="destination link"
                    onSendMessage={onSendMessage}
                  />
                </div>
              ) : null}
            </div>
          )}
          {promotion || leadForm || details?.length ? (
            <details className="border-t pt-2">
              <summary className="w-fit cursor-pointer rounded-sm text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                View details
              </summary>
              <div className="mt-3 space-y-3">
                {promotion || leadForm ? (
                  <dl className="space-y-2 text-xs">
                    {promotion ? (
                      <AdResourceDetail
                        label="Promotion"
                        entity="promotion"
                        resource={promotion}
                        adName={adName}
                        onSendMessage={onSendMessage}
                      />
                    ) : null}
                    {leadForm ? (
                      <AdResourceDetail
                        label="Lead form"
                        entity="lead-form"
                        resource={leadForm}
                        adName={adName}
                        onSendMessage={onSendMessage}
                      />
                    ) : null}
                  </dl>
                ) : null}
                {details?.map((section, index) => (
                  <section
                    key={`${index}-${section.title}`}
                    className="space-y-2"
                  >
                    <h3 className="text-xs font-medium">{section.title}</h3>
                    <dl className="space-y-1.5 text-xs">
                      {section.fields.map((field, fieldIndex) => (
                        <div
                          key={`${fieldIndex}-${field.label}`}
                          className="flex justify-between gap-4"
                        >
                          <dt className="shrink-0 text-muted-foreground">
                            {field.label}
                          </dt>
                          <dd className="min-w-0 whitespace-pre-wrap break-words text-right">
                            {field.value}
                            <AdCardEditButton
                              adName={adName}
                              field={field.label.toLowerCase()}
                              onSendMessage={onSendMessage}
                            />
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      </article>

      {isVideo && blobUrl && (
        <VideoPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          videoUrl={blobUrl}
          thumbnailUrl={thumbnailUrl}
          title={videoTitle ?? adName}
        />
      )}
    </>
  );
}

function AdResourceDetail({
  label,
  entity,
  resource,
  adName,
  onSendMessage,
}: {
  label: string;
  entity: 'promotion' | 'lead-form';
  adName: string;
  onSendMessage?: (text: string) => void;
  resource: { id?: string; name: string };
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 pt-1 text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 items-start gap-1 text-right">
        <span className="min-w-0 break-words pt-1">{resource.name}</span>
        {onSendMessage ? (
          <AdCardEditButton
            adName={adName}
            field={label.toLowerCase()}
            onSendMessage={onSendMessage}
          />
        ) : resource.id ? (
          <Link
            to="/edit/$entity/$id"
            params={{ entity, id: resource.id }}
            aria-label={`Edit ${label.toLowerCase()}: ${resource.name}`}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="size-3.5" aria-hidden="true" />
          </Link>
        ) : null}
      </dd>
    </div>
  );
}

/** Scopes the card's inline edit controls to this ad. */
export function AdPreviewCard(props: AdPreviewProps) {
  const current = useSavedAdPreview(props);
  return (
    <AdCardEditTargetContext.Provider value={props.adId}>
      <AdPreviewCardContent {...current} />
    </AdCardEditTargetContext.Provider>
  );
}
